/**
 * Cache utilities for managing permission-based data
 */

/**
 * Generic function to handle cached API requests with permission versioning
 * @param {Function} apiCall - The API function to call
 * @param {Function} dataTransformer - Function to transform the response data
 * @param {Function} setStateCallback - Function to set the state with transformed data
 * @param {string} cacheKey - Cache key for localStorage
 * @param {...any} apiParams - Parameters to pass to the API call
 */
export const handleCachedApiRequest = (
	apiCall,
	dataTransformer,
	setStateCallback,
	cacheKey,
	...apiParams
) => {
	apiCall(...apiParams).then((res) => {
		if (res.data.success) {
			const currentVersion = res.data.permission_version;

			// Use cached data if it's valid
			if (isCacheValid(cacheKey, currentVersion)) {
				setStateCallback(JSON.parse(localStorage.getItem(cacheKey)));
				return;
			}

			// Transform and cache new data
			const transformedData = dataTransformer(res.data);

			setStateCallback(transformedData);
			localStorage.setItem(cacheKey, JSON.stringify(transformedData));

			// Store permission version for cache invalidation
			if (currentVersion) {
				localStorage.setItem(`${cacheKey}_version`, currentVersion);
			}
		}
	});
};

/**
 * Clear all permission-related cache for a user
 * @param {string} userId - User ID for user-specific cache keys
 */
export const clearPermissionCache = (userId) => {
	const permissionCacheKeys = [
		`areaOptionsOffMarket_${userId}`,
		`areaOptionsActive_${userId}`,
		`areaOptionsForeclosure_${userId}`,
		`priceAreaOptions_${userId}`,
		`subAreaOptions_${userId}`,
		// Version keys
		`areaOptionsOffMarket_${userId}_version`,
		`areaOptionsActive_${userId}_version`,
		`areaOptionsForeclosure_${userId}_version`,
		`priceAreaOptions_${userId}_version`,
		`subAreaOptions_${userId}_version`
	];

	permissionCacheKeys.forEach((key) => {
		localStorage.removeItem(key);
	});

	console.log('Permission cache cleared for user:', userId);
};

/**
 * Clear all permission-related cache (for all users)
 */
export const clearAllPermissionCache = () => {
	const keys = Object.keys(localStorage);
	const permissionKeys = keys.filter(
		(key) =>
			key.includes('areaOptions') ||
			key.includes('subAreaOptions') ||
			key.includes('_version')
	);

	permissionKeys.forEach((key) => {
		localStorage.removeItem(key);
	});

	console.log('All permission cache cleared');
};

/**
 * Check if cached data is valid based on permission version
 * @param {string} cacheKey - The cache key to check
 * @param {string} currentVersion - Current permission version from server
 * @returns {boolean} - Whether cached data is valid
 */
export const isCacheValid = (cacheKey, currentVersion) => {
	const cachedData = localStorage.getItem(cacheKey);
	if (!cachedData) return false;

	// No versioning for non-permission data - assume valid if exists
	if (!currentVersion) return true;

	const cachedVersion = localStorage.getItem(`${cacheKey}_version`);
	const isValid = cachedVersion === currentVersion;

	// Clean up stale cache if invalid
	if (!isValid) {
		localStorage.removeItem(cacheKey);
		localStorage.removeItem(`${cacheKey}_version`);
	}

	return isValid;
};
